import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Token keys matching AuthContext
const TOKEN_KEY = 'pp_token';
const USER_KEY = 'pp_user';
const ADMIN_TOKEN_KEY = 'pp_admin_token';

// Helper to get token from either storage
const getToken = () => {
  return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
};

const getAdminToken = () => {
  return sessionStorage.getItem(ADMIN_TOKEN_KEY) || localStorage.getItem(ADMIN_TOKEN_KEY);
};

const clearUserAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  // Skip adding user token for admin routes (they use adminToken)
  if (config.url?.includes('/admin/')) {
    return config;
  }
  
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect admin routes to user login
    if (error.response?.status === 401 && !error.config?.url?.includes('/admin/')) {
      clearUserAuth();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me')
};

// Jurisdictions API
export const jurisdictionsAPI = {
  getAll: () => api.get('/jurisdictions')
};

// Questions API
export const questionsAPI = {
  getQuestions: (jurisdiction, assessmentType) => api.get(`/questions/${jurisdiction}/${assessmentType}`)
};

// Assessments API
export const assessmentsAPI = {
  create: (data) => api.post('/assessments', data),
  getAll: () => api.get('/assessments'),
  getById: (id) => api.get(`/assessments/${id}`),
  submitAnswers: (id, answers) => api.post(`/assessments/${id}/answers`, { assessment_id: id, answers }),
  saveProgress: (id, currentCategoryIndex) => api.post(`/assessments/${id}/save-progress`, { current_category_index: currentCategoryIndex }),
  checkApplicability: (id) => api.post(`/assessments/${id}/check-applicability`),
  completeNonApplicable: (id) => api.post(`/assessments/${id}/complete-non-applicable`),
  complete: (id) => api.post(`/assessments/${id}/complete`),
  downloadPDF: (id) => api.get(`/assessments/${id}/pdf`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/assessments/${id}`)
};

// Admin API
export const adminAPI = {
  login: (data) => api.post('/admin/login', data),
  getStats: () => api.get('/admin/stats'),
  getUsers: () => api.get('/admin/users'),
  getAssessments: (params) => api.get('/admin/assessments', { params }),
  getAssessmentDetail: (id) => api.get(`/admin/assessments/${id}`),
  downloadPDF: (id) => api.get(`/admin/assessments/${id}/pdf`, { responseType: 'blob' }),
  updateFollowUp: (userId, data) => api.put(`/admin/users/${userId}/followup`, data),
  deleteNote: (userId, noteIndex) => api.delete(`/admin/users/${userId}/followup/notes/${noteIndex}`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`)
};

// Export token key for admin components
export { ADMIN_TOKEN_KEY };

export default api;
